/**
 * Turnifly — Zod Schemas & TypeScript Types
 * Auto-derived from database schema (56 tables)
 * Use these for ALL external data validation — NEVER use `as Type`
 */

import { z } from 'zod'

// ============================================================
// PROFILES
// ============================================================

export const BadgeTypeEnum = z.enum(['expert', 'organization', 'company'])

export const ProfileSchema = z.object({
  id: z.string().uuid(),
  telegram_id: z.number(),
  phone: z.string().nullable(),
  full_name: z.string().min(3).max(40),
  avatar_url: z.string().nullable(),
  gender: z.enum(['male', 'female']).nullable(),
  bio: z.string().max(300).nullable(),
  link_title: z.string().max(50).nullable(),
  link_url: z.string().url().nullable().or(z.literal('')).or(z.null()),
  profession: z.string().nullable(),
  interests: z.array(z.string()).default([]),
  country: z.string().default('Uzbekistan'),
  timezone: z.string().default('Asia/Tashkent'),
  birth_date: z.string().nullable(),
  life_expectancy: z.number().min(50).max(120).default(80),
  is_private: z.boolean().default(false),
  show_region: z.boolean().default(true),
  show_gender: z.boolean().default(true),
  show_age: z.boolean().default(true),
  is_premium: z.boolean().default(false),
  badge_type: BadgeTypeEnum.nullable(),
  verified_name: z.string().nullable(),
  verified_username: z.string().nullable(),
  username: z.string().min(5).max(32).regex(/^[a-z0-9_]+$/).nullable(),
  referral_code: z.string(),
  referred_by: z.string().uuid().nullable(),
  followers_count: z.number().default(0),
  following_count: z.number().default(0),
  goals_count: z.number().default(0),
  language: z.enum(['uz', 'en', 'ru']).default('uz'),
  notifications_enabled: z.boolean().default(true),
  dark_mode: z.boolean().nullable(),
  onboarding_completed: z.boolean().default(false),
  interests_asked: z.boolean().default(false),
  interests_ask_date: z.string().nullable().optional(),
  garden_type: z.enum(['garden', 'orchard']).default('garden'),
  banner_template: z.string().default('default_1'),
  banner_url: z.string().nullable().default(null),
  banner_position_y: z.number().min(0).max(100).default(50),
  habit_view_mode: z.enum(['monthly', 'weekly']).catch('monthly'),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
})

export type Profile = z.infer<typeof ProfileSchema>

/** Allowed fields for client-side profile updates */
export const ProfileUpdateSchema = ProfileSchema.partial().omit({
  id: true,
  telegram_id: true,
  referral_code: true,
  referred_by: true,
  created_at: true,
  updated_at: true,
  deleted_at: true,
  followers_count: true,
  following_count: true,
  goals_count: true,
  is_premium: true,
  badge_type: true,
  verified_name: true,
  verified_username: true,
})

export type ProfileUpdate = z.infer<typeof ProfileUpdateSchema>

// ============================================================
// FOLLOWS
// ============================================================

export const FollowStatusEnum = z.enum(['accepted', 'pending'])

export const FollowSchema = z.object({
  follower_id: z.string().uuid(),
  following_id: z.string().uuid(),
  status: FollowStatusEnum,
  created_at: z.string(),
})

export type Follow = z.infer<typeof FollowSchema>

// ============================================================
// HABITS
// ============================================================

export const HabitSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  name: z.string().min(1).max(100),
  icon: z.string().default('📌'),
  category: z.string().nullable(),
  description: z.string().max(500).nullable(),
  frequency: z.enum(['daily', 'weekly_days', 'custom_interval']).default('daily'),
  weekly_days: z.array(z.number().min(1).max(7)).nullable(),
  custom_interval: z.number().min(1).max(365).nullable(),
  start_date: z.string(),
  end_date: z.string().nullable(),
  time_mode: z.enum(['anytime', 'duration', 'time_range']).default('anytime'),
  time_duration: z.number().nullable(),
  time_start: z.string().nullable(),
  time_end: z.string().nullable(),
  timezone: z.string().default('Asia/Tashkent'),
  value_required: z.boolean().default(false),
  value_target: z.number().nullable(),
  value_unit: z.string().nullable(),
  value_comparison: z.enum(['gte', 'lte']).default('gte'),
  proof_types: z.array(z.enum(['text', 'image', 'video'])).default([]),
  penalty_text: z.string().nullable(),
  reminder_enabled: z.boolean().default(false),
  reminder_on_miss: z.boolean().default(false),
  reminder_miss_text: z.string().nullable(),
  goal_id: z.string().uuid().nullable(),
  challenge_id: z.string().uuid().nullable(),
  sort_order: z.number().default(0),
  is_active: z.boolean().default(true),
  created_at: z.string(),
  updated_at: z.string(),
})

export type Habit = z.infer<typeof HabitSchema>

export const HabitCreateSchema = HabitSchema.omit({
  id: true,
  user_id: true,
  created_at: true,
  updated_at: true,
})

export type HabitCreate = z.infer<typeof HabitCreateSchema>

export const HabitLogSchema = z.object({
  user_id: z.string().uuid(),
  habit_id: z.string().uuid(),
  log_date: z.string(),
  completed: z.boolean().default(true),
  value: z.number().nullable(),
  proof_url: z.string().nullable(),
  proof_type: z.enum(['text', 'image', 'video']).nullable(),
  notes: z.string().nullable(),
  completed_at: z.string(),
})

export type HabitLog = z.infer<typeof HabitLogSchema>

export const HabitStreakSchema = z.object({
  user_id: z.string().uuid(),
  habit_id: z.string().uuid(),
  current_streak: z.number().default(0),
  longest_streak: z.number().default(0),
  streak_start_date: z.string().nullable(),
  last_completed_date: z.string().nullable(),
})

export type HabitStreak = z.infer<typeof HabitStreakSchema>

// Monthly habits RPC — single habit log entry
export const MonthlyHabitLogSchema = z.object({
  log_date: z.string(),
  completed: z.boolean(),
  value: z.number().nullable().catch(null),
  proof_url: z.string().nullable().catch(null),
})

export type MonthlyHabitLog = z.infer<typeof MonthlyHabitLogSchema>

// Monthly habits RPC — streak info
export const MonthlyHabitStreakSchema = z.object({
  current: z.number().catch(0),
  longest: z.number().catch(0),
  last_completed_date: z.string().nullable().optional().catch(null),
})

export type MonthlyHabitStreak = z.infer<typeof MonthlyHabitStreakSchema>

// Frequency type — string from DB, typed for frontend logic
export type HabitFrequency = 'daily' | 'weekly_days' | 'custom_interval'

// Monthly habits RPC — single habit with logs and streak
export const MonthlyHabitItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  icon: z.string().catch('📌'),
  category: z.string().nullable().catch(null),
  description: z.string().nullable().optional().catch(null),
  frequency: z.string().catch('daily'),
  weekly_days: z.array(z.number()).nullable().catch(null),
  custom_interval: z.number().nullable().catch(null),
  start_date: z.string().optional().catch(undefined),
  end_date: z.string().nullable().optional().catch(null),
  time_mode: z.string().nullable().catch(null),
  time_start: z.string().nullable().catch(null),
  time_end: z.string().nullable().optional().catch(null),
  time_duration: z.number().nullable().catch(null),
  value_required: z.boolean().catch(false),
  value_target: z.number().nullable().catch(null),
  value_unit: z.string().nullable().catch(null),
  value_comparison: z.string().nullable().catch(null),
  proof_types: z.array(z.string()).catch([]),
  penalty_text: z.string().nullable().catch(null),
  reminder_enabled: z.boolean().nullable().catch(false),
  goal_id: z.string().nullable().catch(null),
  challenge_id: z.string().nullable().catch(null),
  sort_order: z.number().catch(0),
  logs: z.array(MonthlyHabitLogSchema).catch([]),
  streak: MonthlyHabitStreakSchema.catch({ current: 0, longest: 0 }),
})

export type MonthlyHabitItem = z.infer<typeof MonthlyHabitItemSchema>

// Monthly habits RPC — daily stats entry
export const DailyStatSchema = z.object({
  stat_date: z.string(),
  total_habits: z.number().catch(0),
  completed_habits: z.number().catch(0),
  completion_rate: z.number().catch(0),
})

export type DailyStat = z.infer<typeof DailyStatSchema>

// Monthly habits RPC — full response
export const MonthlyHabitsResponseSchema = z.object({
  habits: z.array(MonthlyHabitItemSchema).catch([]),
  daily_stats: z.array(DailyStatSchema).catch([]),
})

export type MonthlyHabitsResponse = z.infer<typeof MonthlyHabitsResponseSchema>

// ============================================================
// GOALS
// ============================================================

export const GoalStepSchema = z.object({
  label: z.string().min(1).max(200),
})

export type GoalStep = z.infer<typeof GoalStepSchema>

export const GoalSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  name: z.string().min(1).max(100),
  icon: z.string().default('🎯'),
  category: z.string().nullable(),
  description: z.string().max(500).nullable(),
  cover_image_url: z.string().nullable(),
  year: z.number().min(2024).max(2100),
  goal_type: z.enum(['numeric', 'steps']).default('numeric'),
  target_value: z.number().positive(),
  current_value: z.number().default(0),
  start_value: z.number().default(0),
  unit: z.string(),
  direction: z.enum(['increase', 'decrease']).default('increase'),
  steps: z.array(GoalStepSchema).nullable().catch(null),
  milestones: z.any().default([]),
  auto_milestones: z.boolean().default(true),
  weekly_milestones: z.boolean().default(false),
  status: z.enum(['planned', 'in_progress', 'achieved', 'incomplete', 'archived']).default('planned'),
  achieved_at: z.string().nullable(),
  is_private: z.boolean().default(false),
  is_announced: z.boolean().default(false),
  monthly_reminder: z.boolean().default(false),
  deadline: z.string().nullable(),
  sort_order: z.number().default(0),
  created_at: z.string(),
  updated_at: z.string(),
})

export type Goal = z.infer<typeof GoalSchema>

export const GoalCreateSchema = GoalSchema.omit({
  id: true,
  user_id: true,
  current_value: true,
  achieved_at: true,
  created_at: true,
  updated_at: true,
})

export type GoalCreate = z.infer<typeof GoalCreateSchema>

// ── Goal Entries ──

export const GoalEntrySchema = z.object({
  id: z.string().uuid(),
  goal_id: z.string().uuid(),
  user_id: z.string().uuid(),
  value: z.number().default(1),
  step_index: z.number().nullable().default(null),
  note: z.string().default(''),
  is_shared: z.boolean().default(false),
  entry_date: z.string(),
  created_at: z.string(),
})

export type GoalEntry = z.infer<typeof GoalEntrySchema>

// ============================================================
// CHALLENGES
// ============================================================

export const ChallengeSchema = z.object({
  id: z.string().uuid(),
  creator_id: z.string().uuid(),
  type: z.enum(['private', 'public', 'competition', 'platform_free', 'platform_pro']),
  time_model: z.enum(['fixed', 'evergreen']),
  name: z.string().min(3).max(80),
  description: z.string().max(300).nullable(),
  category_id: z.string().nullable(),
  cover_image_url: z.string().nullable(),
  cover_emoji: z.string().nullable(),
  cover_gradient: z.string().nullable(),
  link_title: z.string().nullable(),
  link_url: z.string().nullable(),
  tags: z.array(z.string().max(30)).max(10).default([]),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  duration_days: z.number().min(1).max(30),
  gender_filter: z.enum(['all', 'female', 'male']).default('all'),
  max_participants: z.number().nullable(),
  creator_participates: z.boolean().default(true),
  join_policy: z.enum(['before_start', 'first_3_days', 'anytime']).default('before_start'),
  is_paid: z.boolean().default(false),
  price: z.number().min(10000).max(1000000).nullable(),
  commission_rate: z.number().default(0.25),
  join_conditions: z.any().default({}),
  is_hidden: z.boolean().default(false),
  members_count: z.number().default(0),
  avg_rating: z.number().nullable(),
  reviews_count: z.number().default(0),
  status: z.enum(['draft', 'upcoming', 'active', 'completed', 'cancelled']).default('draft'),
  short_code: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

export type Challenge = z.infer<typeof ChallengeSchema>

export const ChallengeCreateSchema = z.object({
  type: z.enum(['private', 'public', 'competition']),
  time_model: z.enum(['fixed', 'evergreen']),
  name: z.string().min(3).max(80),
  description: z.string().max(300).optional(),
  category_id: z.string().optional(),
  cover_image_url: z.string().optional(),
  cover_emoji: z.string().optional(),
  cover_gradient: z.string().optional(),
  link_title: z.string().max(50).optional(),
  link_url: z.string().url().optional(),
  tags: z.array(z.string().max(30)).max(10).default([]),
  start_date: z.string().optional(),
  duration_days: z.number().min(1).max(30),
  gender_filter: z.enum(['all', 'female', 'male']).default('all'),
  max_participants: z.number().optional(),
  creator_participates: z.boolean().default(true),
  join_policy: z.enum(['before_start', 'first_3_days', 'anytime']).default('before_start'),
  is_paid: z.boolean().default(false),
  price: z.number().min(10000).max(1000000).optional(),
  join_conditions: z.object({
    follow_creator: z.boolean().default(false),
    like: z.boolean().default(false),
    comment: z.boolean().default(false),
    share: z.boolean().default(false),
    repost: z.boolean().default(false),
  }).default({
    follow_creator: false,
    like: false,
    comment: false,
    share: false,
    repost: false,
  }),
  prizes: z.array(z.object({
    place_from: z.number(),
    place_to: z.number(),
    prize_type: z.enum(['money', 'premium', 'telegram_stars', 'physical', 'custom']),
    prize_value: z.string(),
    prize_description: z.string().optional(),
  })).optional(),
  is_hidden: z.boolean().default(false),
})

export type ChallengeCreate = z.infer<typeof ChallengeCreateSchema>

export const ChallengeMemberSchema = z.object({
  id: z.string().uuid(),
  challenge_id: z.string().uuid(),
  user_id: z.string().uuid(),
  attempt_number: z.number().default(1),
  status: z.enum(['active', 'completed', 'failed', 'left']).default('active'),
  joined_at: z.string(),
  personal_start_date: z.string().nullable().optional(),
  personal_end_date: z.string().nullable().optional(),
  completion_rate: z.number().nullable().default(null),
  completed_at: z.string().nullable().optional(),
  payment_id: z.string().uuid().nullable().optional(),
  created_at: z.string(),
})

export type ChallengeMember = z.infer<typeof ChallengeMemberSchema>

// ============================================================
// POSTS & FEED
// ============================================================

export const PostMetadataSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).catch({})

export const PostSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  type: z.string().default('user_post'),
  content: z.string().max(2000).nullable(),
  media_urls: z.array(z.string()).catch([]),
  media_type: z.enum(['image', 'video', 'progress_card']).nullable(),
  original_post_id: z.string().uuid().nullable(),
  challenge_id: z.string().uuid().nullable(),
  goal_id: z.string().uuid().nullable(),
  achievement_id: z.string().uuid().nullable(),
  metadata: PostMetadataSchema,
  likes_count: z.number().default(0),
  comments_count: z.number().default(0),
  reposts_count: z.number().default(0),
  score: z.number().default(0),
  is_hidden: z.boolean().default(false),
  created_at: z.string(),
  updated_at: z.string(),
})

export type Post = z.infer<typeof PostSchema>

export const PostCreateSchema = z.object({
  content: z.string().max(2000).optional(),
  media_urls: z.array(z.string()).max(4).default([]),
  media_type: z.enum(['image', 'video', 'progress_card']).optional(),
  metadata: PostMetadataSchema.optional(),
})

export type PostCreate = z.infer<typeof PostCreateSchema>

export const CommentSchema = z.object({
  id: z.string().uuid(),
  post_id: z.string().uuid(),
  user_id: z.string().uuid(),
  parent_id: z.string().uuid().nullable(),
  text: z.string().min(1).max(1000),
  likes_count: z.number().default(0),
  is_quick: z.boolean().default(false),
  created_at: z.string(),
})

export type Comment = z.infer<typeof CommentSchema>

export const CommentWithAuthorSchema = z.object({
  id: z.string().uuid(),
  post_id: z.string().uuid(),
  user_id: z.string().uuid(),
  parent_id: z.string().uuid().nullable(),
  text: z.string(),
  likes_count: z.number().catch(0),
  is_quick: z.boolean().catch(false),
  created_at: z.string(),
  author_name: z.string().catch(''),
  author_avatar: z.string().nullable().catch(null),
  author_badge: z.string().nullable().catch(null),
  is_liked_by_me: z.boolean().catch(false),
})

export type CommentWithAuthor = z.infer<typeof CommentWithAuthorSchema>

export const QuickCommentSchema = z.object({
  id: z.number(),
  text_uz: z.string(),
  text_en: z.string(),
  context: z.string(),
  icon: z.string().catch(''),
  sort_order: z.number(),
})

export type QuickComment = z.infer<typeof QuickCommentSchema>

// Original post embedded in repost
export const OriginalPostSchema = z.object({
  id: z.string().uuid(),
  content: z.string().nullable().catch(null),
  author_name: z.string().catch(''),
  author_avatar: z.string().nullable().catch(null),
  author_badge: z.string().nullable().catch(null),
  author_id: z.string().uuid().optional(),
  media_urls: z.union([z.array(z.string()), z.null()]).catch(null),
  media_type: z.string().nullable().catch(null),
  type: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().catch(null),
}).nullable().catch(null)

// Feed item (from RPC functions)
export const FeedItemSchema = z.object({
  post_id: z.string().uuid(),
  post_type: z.string(),
  content: z.string().nullable(),
  media_urls: z.union([z.array(z.string()), z.null()]).catch([]),
  media_type: z.string().nullable().catch(null),
  created_at: z.string(),
  likes_count: z.number(),
  comments_count: z.number(),
  reposts_count: z.number(),
  views_count: z.number().catch(0),
  author_id: z.string().uuid(),
  author_name: z.string(),
  author_avatar: z.string().nullable(),
  author_badge: z.string().nullable(),
  is_liked_by_me: z.boolean(),
  score: z.number().optional(),
  is_reposted_by_me: z.boolean().optional(),
  original_post: OriginalPostSchema.optional(),
  metadata: PostMetadataSchema.optional(),
})

export type FeedItem = z.infer<typeof FeedItemSchema>

// ============================================================
// NOTIFICATIONS
// ============================================================

export const NotificationSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  type: z.string(),
  title: z.string(),
  body: z.string().nullable(),
  batch_key: z.string().nullable(),
  actors: z.any().default([]),
  actors_count: z.number().default(1),
  target_type: z.string().nullable(),
  target_id: z.string().nullable(),
  is_read: z.boolean().default(false),
  created_at: z.string(),
})

export type Notification = z.infer<typeof NotificationSchema>

// ============================================================
// PAYMENTS & SUBSCRIPTIONS
// ============================================================

export const SubscriptionSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  plan: z.enum(['monthly', 'yearly']),
  payment_method: z.enum(['subscription', 'one_time']),
  status: z.enum(['active', 'expired', 'cancelled']),
  starts_at: z.string(),
  expires_at: z.string(),
  auto_renew: z.boolean(),
  card_last4: z.string().nullable(),
  card_brand: z.string().nullable(),
  created_at: z.string(),
})

export type Subscription = z.infer<typeof SubscriptionSchema>

export const PricingSchema = z.object({
  id: z.string(),
  plan: z.string(),
  period: z.string(),
  currency: z.string(),
  amount: z.number(),
  country: z.string(),
  is_active: z.boolean(),
})

export type Pricing = z.infer<typeof PricingSchema>

// ============================================================
// COMMON / SHARED
// ============================================================

export const CategorySchema = z.object({
  id: z.string(),
  name_uz: z.string(),
  name_en: z.string(),
  name_ru: z.string().nullable(),
  icon: z.string(),
  sort_order: z.number(),
})

export type Category = z.infer<typeof CategorySchema>

export const QuoteSchema = z.object({
  id: z.string().uuid(),
  text_uz: z.string(),
  text_en: z.string(),
  text_ru: z.string().nullable(),
  author: z.string(),
  likes_count: z.number(),
  shown_date: z.string().nullable(),
})

export type Quote = z.infer<typeof QuoteSchema>

export const AchievementSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name_uz: z.string(),
  name_en: z.string(),
  icon: z.string(),
  category: z.string(),
  target_value: z.number(),
  sort_order: z.number(),
})

export type Achievement = z.infer<typeof AchievementSchema>

export const UserAchievementSchema = z.object({
  user_id: z.string().uuid(),
  achievement_id: z.string().uuid(),
  year: z.number(),
  progress: z.number().min(0),
  status: z.enum(['in_progress', 'achieved']),
  achieved_at: z.string().nullable(),
})

export type UserAchievement = z.infer<typeof UserAchievementSchema>

// ============================================================
// TASK BOARDS
// ============================================================

export const TaskItemSchema = z.object({
  id: z.string().uuid(),
  board_id: z.string().uuid(),
  user_id: z.string().uuid(),
  text: z.string().min(1).max(200),
  completed: z.boolean().default(false),
  sort_order: z.number().default(0),
  completed_at: z.string().nullable().optional(),
  created_at: z.string(),
})

export type TaskItem = z.infer<typeof TaskItemSchema>

export const TaskBoardSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  name: z.string().min(1).max(100),
  sort_order: z.number().default(0),
  created_at: z.string(),
  items: z.array(TaskItemSchema).default([]),
})

export type TaskBoard = z.infer<typeof TaskBoardSchema>

// ============================================================
// FEEDBACK TEMPLATES
// ============================================================

export const FeedbackResponseSchema = z.object({
  id: z.number().optional(),
  category: z.string(),
  signal: z.string().catch('rate'),
  text_uz: z.string(),
  text_en: z.string(),
  text_ru: z.string().nullable().optional(),
  emoji: z.string(),
  bg_color: z.string(),
  completion_rate: z.number(),
  streak: z.number().catch(0),
  streak_target: z.number().nullable().catch(null),
})

export type FeedbackResponse = z.infer<typeof FeedbackResponseSchema>

// ============================================================
// TODAY'S QUOTE (RPC Response)
// ============================================================

export const TodayQuoteSchema = z.object({
  id: z.string().uuid(),
  text_uz: z.string(),
  text_en: z.string(),
  text_ru: z.string().nullable().optional(),
  author: z.string(),
  likes_count: z.number(),
  is_liked: z.boolean(),
})

export type TodayQuote = z.infer<typeof TodayQuoteSchema>

// ============================================================
// POMODORO
// ============================================================

export const PomodoroSettingsSchema = z.object({
  user_id: z.string().uuid(),
  work_duration: z.number().int().min(1).max(120).catch(25),
  short_break_duration: z.number().int().min(1).max(30).catch(5),
  long_break_duration: z.number().int().min(1).max(60).catch(15),
  sessions_before_long_break: z.number().int().min(1).max(10).catch(4),
  auto_start_breaks: z.boolean().catch(false),
  auto_start_work: z.boolean().catch(false),
  timer_style: z.enum(['circular', 'flat']).catch('circular'),
  background_type: z.enum(['color', 'image', 'gif', 'video']).catch('color'),
  background_value: z.string().catch('#BA4949'),
  sound_enabled: z.boolean().catch(false),
  sound_type: z.enum(['tick', 'rain', 'fire', 'mp3']).catch('tick'),
  sound_url: z.string().nullable().catch(null),
  alarm_sound: z.enum(['bell', 'digital', 'gentle', 'none']).catch('bell'),
  volume: z.number().int().min(0).max(100).catch(80),
})

export type PomodoroSettings = z.infer<typeof PomodoroSettingsSchema>

export const PomodoroSessionSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  session_type: z.enum(['work', 'short_break', 'long_break']),
  planned_minutes: z.number().int(),
  actual_seconds: z.number().int(),
  habit_id: z.string().uuid().nullable().catch(null),
  task_text: z.string().nullable().catch(null),
  completed: z.boolean(),
  started_at: z.string(),
  ended_at: z.string(),
})

export type PomodoroSession = z.infer<typeof PomodoroSessionSchema>

export const PomodoroTaskSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  text: z.string(),
  completed: z.boolean(),
  sort_order: z.number().int(),
  target_pomodoros: z.number().int().min(1).max(20).catch(1),
  completed_pomodoros: z.number().int().catch(0),
  habit_id: z.string().uuid().nullable().catch(null),
  created_at: z.string(),
})

export type PomodoroTask = z.infer<typeof PomodoroTaskSchema>

export const PomodoroStatsSchema = z.object({
  total_focus_minutes: z.number().catch(0),
  total_sessions: z.number().catch(0),
  today_focus_minutes: z.number().catch(0),
  today_sessions: z.number().catch(0),
  current_streak: z.number().catch(0),
  weekly_minutes: z.array(z.object({
    day: z.string(),
    minutes: z.number(),
    sessions: z.number(),
  })).catch([]),
})

export type PomodoroStats = z.infer<typeof PomodoroStatsSchema>

export const PomodoroPresetSchema = z.object({
  id: z.number(),
  type: z.enum(['color', 'image', 'gif', 'video', 'sound']),
  name: z.string(),
  name_uz: z.string().nullable().catch(null),
  name_en: z.string().nullable().catch(null),
  url: z.string().nullable().catch(null),
  thumbnail_url: z.string().nullable().catch(null),
  match_key: z.string().nullable().catch(null),
  is_premium: z.boolean(),
  sort_order: z.number(),
})

export type PomodoroPreset = z.infer<typeof PomodoroPresetSchema>
